import { Autocomplete, Box, Chip, CircularProgress, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import debounce from 'lodash/debounce';
import { useLazyQuery } from '@apollo/client';
import { differenceInYears } from 'date-fns';
import SearchResultItem from '../common/search/SearchResultItem';
import { capitalize, isNaN } from 'lodash';
import { PatientSexEnum, SearchPatientsQuery } from '~/graphql/__generated__/graphql';
import { datef, formatShortDate } from '@caredna-marlin/utils/dates';
import { SEARCH_PATIENTS_QUERY } from './patientSearchQuery';

interface Option {
  title: string;
  id: number;
  icon: JSX.Element;
  subtitle1: string | null | undefined;
  subtitle2: string;
}

const calculateAge = (dateOfBirth: string): number | null => {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  if (isNaN(birthDate.getTime())) return null; // Check if the date is valid

  const today = new Date();
  return differenceInYears(today, birthDate);
};

interface PatientSearchBoxProps {
  onPatientClicked: (patient: NonNullable<Option>) => void;
  emptyTextInputAfterSelect?: boolean;
}

const generatePatientTitle = (patient: { firstName: string, lastName: string, sex: PatientSexEnum | null, dateOfBirth: string | null }) => {
  const { firstName, lastName, sex, dateOfBirth } = patient;

  const sexString = sex ? `• ${capitalize(sex)}` : '';

  const dobString = dateOfBirth
    ? `• ${calculateAge(dateOfBirth)}yo (${formatShortDate(dateOfBirth)})`
    : '';

  return `${firstName} ${lastName} ${sexString} ${dobString}`.trim();
};

const PatientSearchBox: React.FC<PatientSearchBoxProps> = ({ onPatientClicked, emptyTextInputAfterSelect = true }) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<Option | null>(null);
  const [searchTriggered, setSearchTriggered] = useState(false); // Track if a search was triggered

  const [searchPatients, { data, loading }] = useLazyQuery<SearchPatientsQuery>(SEARCH_PATIENTS_QUERY);

  const notFoundItems = useMemo(() => [{ title: '', id: -1, icon: <NoAccountsIcon />, subtitle1: '', subtitle2: '' }], []);

  const getChipForResult = (episode: { active: boolean, createdAt: string, updatedAt: string } | undefined | null): React.ReactElement | undefined => {
    const dateFmt = "M/d/yy";
    if(!episode) {
      return undefined;
    }
    if(episode.active) {
      const activeTitle = `Current patient since ${datef(episode.createdAt, dateFmt)}`;
      return <Tooltip title={activeTitle} placement='right'>
        <Chip label="Active" size='small' color='primary' />
      </Tooltip>
    }
    const formerTitle = `Former patient as of ${datef(episode.updatedAt, dateFmt)}`;
    return <Tooltip title={formerTitle} placement='right'>
      <Chip label="Former" size='small' color='primary' variant="outlined"  />
    </Tooltip> 
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        const searchTerm = inputValue;
        if (inputValue.trim() !== '') {
          setSearchTriggered(true);
          searchPatients({
            variables: {
              searchTerm,
              limit: 10,
              offset: 0,
            },
          });
        }
      }, 300),
    [searchPatients]
  );

  const options = useMemo(() => {
    if (!data?.searchPatient || !searchTriggered) {
      return [];
    }
    if (data.searchPatient.length === 0) {
      return notFoundItems;
    }

    const patientOptions: Option[] = data.searchPatient.map((result) => {
      return {
        title: generatePatientTitle(result.patient),
        id: result.patient.id,
        icon: <PersonIcon />,
        subtitle1: result.patient.phone && `Phone: ${result.patient.phone?.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}` || "",
        subtitle2: ``,
        chip: getChipForResult(result.bestEpisode),
      }
    });
    return patientOptions;
  }, [data, notFoundItems, searchTriggered]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string, reason: string) => {
      if (reason === 'reset') {
        setInputValue('');
        setSearchTriggered(false);
        setSelectedValue(null); 
      } else {
        setInputValue(value);
        if (value.length >= 3) {
          debouncedSearch(value);
        } else {
          setSearchTriggered(false);
        }
      }
    },
    [debouncedSearch, setSearchTriggered]
  );

  const handleOptionSelect = (_event: React.SyntheticEvent, value: string | Option | null) => {
    if (value && typeof value === 'object' && value.id) {
      setInputValue(emptyTextInputAfterSelect ? '' : value.title);
      onPatientClicked(value);
      setSelectedValue(null);
    }
  };

  return (
    <Box sx={{ minWidth: 500 }}>
      <Autocomplete
        freeSolo
        id="free-solo-2-demo"
        options={options}
        value={selectedValue}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        fullWidth
        size="small"
        onChange={handleOptionSelect}
        filterOptions={(x) => x}
        loading={loading}
        loadingText='Searching...'
        renderInput={(params) => (
          <TextField
            variant="outlined"
            sx={{
              backgroundColor: 'white',
              borderRadius: '4px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderRadius: 'inherit',
                },
              },
              '& .MuiInputBase-input': {
                color: 'gray',
              },
            }}
            {...params}
            placeholder='Search patients by name, date of birth or phone number'
            slotProps={{ input: {
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? <CircularProgress size={20} /> : <SearchIcon />}
                </InputAdornment>
              ),
            }}}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...restProps } = props;

          if (option.id === -1 && searchTriggered && !loading) {
            return <NoItems key={option.id} {...restProps} />;
          }
          return (
            <SearchResultItem
              key={option.id /* preferred over the key mui gives us because it's a db key with guaranteed no duplicates */}
              itemProps={restProps}
              inputValue={inputValue}
              {...option}
            />
          );
        }}
      />
    </Box>
  );
};

const NoItems = (props: any) => {
  return <Box component="li" {...props} onClick={() => {}}>
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
      <PersonIcon />
    </Box>
    <Typography variant="body2" color="textSecondary">
      No matches found
    </Typography>
  </Box>
}

export default PatientSearchBox;
